/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { GlobalPositionStrategy, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Directive, EventEmitter, Input, NgZone, Output, ViewContainerRef } from '@angular/core';
import { MatMenu, MatMenuPanel } from '@angular/material/menu';
import {
  CONTEXT_MENU_OPEN_DEFER_MS,
  CONTEXT_MENU_OUTSIDE_SUBSCRIBE_DEFER_MS,
} from '@gh/core-ui';
import { toPx } from '@gh/core-util';
import { CellContextMenuEvent, Column, GridOptions, IRowNode } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import {
  GRID_FEATURE,
  GridFeature,
  GridFeatureInitParams,
  GridFeatureReadyParams,
} from '../feature.interfaces';

export interface GridContextChangeEvent {
  node: IRowNode;
  data: any;
}

/**
 * This code was mainly copy-pasted from @angular/material library (MdMenuTrigger directive)
 */
@Directive({
  standalone: false,
  selector: '[ghGridContextMenu]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridContextMenuDirective,
    multi: true,
  }],
  exportAs: 'ghGridContextMenu',
})
export class GridContextMenuDirective implements GridFeature {
  /** References the menu instance that the trigger is associated with. */
  @Input('ghGridContextMenu') menu: MatMenuPanel;

  @Output() contextChange = new EventEmitter<GridContextChangeEvent>();

  data?: any;
  node?: IRowNode;

  private element: HTMLElement;
  private viewContainerRef: ViewContainerRef;
  private _portal: TemplatePortal<any>;
  private overlayRef: Maybe<OverlayRef>;
  private _menuOpen: boolean = false;
  private previousOnCellContextMenu?: GridOptions['onCellContextMenu'];
  private _outsidePointerUnlisten?: () => void;
  private _hostContextMenuUnlisten?: () => void;
  private _openDeferId?: ReturnType<typeof setTimeout>;
  private _outsideSubscribeDeferId?: ReturnType<typeof setTimeout>;
  private _menuCloseSubscription?: Subscription;
  private _destroyed = false;

  constructor(
    private overlay: Overlay,
    private ngZone: NgZone,
  ) {
  }

  initGrid(params: GridFeatureInitParams): void {
    this.element = params.nativeElement;
    this.viewContainerRef = params.viewContainerRef;
    const blockBrowserMenu = (e: Event) => {
      e.preventDefault();
    };
    this.element.addEventListener('contextmenu', blockBrowserMenu, true);
    this._hostContextMenuUnlisten = () =>
      this.element.removeEventListener('contextmenu', blockBrowserMenu, true);
  }

  enhanceGridOptions(options: GridOptions): void {
    this.previousOnCellContextMenu = options.onCellContextMenu;
    options.suppressContextMenu = true;
    options.onCellContextMenu = (e: CellContextMenuEvent) => {
      if (e.event) {
        e.event.preventDefault();
        e.event.stopPropagation();
      }
      if (this.previousOnCellContextMenu) {
        this.previousOnCellContextMenu(e);
      }
      if (e.node && e.event) {
        const ev = e.event;
        if (ev instanceof MouseEvent) {
          this.showMenu(e.node, e.column, e.value, ev);
        }
      }
    };
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this._menuCloseSubscription?.unsubscribe();
    this._menuCloseSubscription = this.menu.close.subscribe(() => this.closeMenu());
  }

  onDestroy(): void {
    this._destroyed = true;
    this._menuCloseSubscription?.unsubscribe();
    this._menuCloseSubscription = void 0;
    if (this._hostContextMenuUnlisten) {
      this._hostContextMenuUnlisten();
      this._hostContextMenuUnlisten = void 0;
    }
    this.destroyMenu();
  }

  showMenu(node: IRowNode, column: Column, value: any, mouseEvent: MouseEvent): void {
    this.node = node;
    this.data = node.data;

    this.contextChange.emit({ node, data: node.data });

    this.openMenu(mouseEvent);
  }

  hideActiveMenu(): void {
    this.closeMenu();
  }

  /** Opens the menu. */
  openMenu(event: MouseEvent): void {
    if (this._menuOpen) {
      return;
    }
    this._clearOpenTimers();
    const clientX = event.clientX;
    const clientY = event.clientY;
    // Defer attach until after the contextmenu gesture: opening in the same turn as contextmenu
    // lets Material/CDK treat the trailing pointer release as an outside interaction (menu flashes).
    this._openDeferId = setTimeout(() => {
      this._openDeferId = void 0;
      if (this._destroyed) {
        return;
      }
      if (this._menuOpen) {
        return;
      }
      const positionEvent = { clientX, clientY } as MouseEvent;
      this._createOverlay(positionEvent).attach(this._portal);
      this._initMenu();
      this._outsideSubscribeDeferId = setTimeout(() => {
        this._outsideSubscribeDeferId = void 0;
        if (!this._destroyed) {
          this._subscribeOutsidePointerClose();
        }
      }, CONTEXT_MENU_OUTSIDE_SUBSCRIBE_DEFER_MS);
    }, CONTEXT_MENU_OPEN_DEFER_MS);
  }

  /** Closes the menu. */
  closeMenu(): void {
    this._clearOpenTimers();
    if (!this._menuOpen) {
      return;
    }
    this._setIsMenuOpen(false);
    this.data = void 0;
    this.node = void 0;

    this._unsubscribeOutsidePointerClose();
    if (this.overlayRef) {
      this._setMatMenuPanelOpen(false);
      this.overlayRef.detach();
    }
  }

  /** Removes the menu from the DOM. */
  destroyMenu(): void {
    this._clearOpenTimers();
    this._unsubscribeOutsidePointerClose();
    if (this.overlayRef) {
      this._setMatMenuPanelOpen(false);
      this.overlayRef.dispose();
      this.overlayRef = void 0;
    }
    this.data = void 0;
    this.node = void 0;
    this._setIsMenuOpen(false);
  }

  private _clearOpenTimers(): void {
    if (this._openDeferId !== void 0) {
      clearTimeout(this._openDeferId);
      this._openDeferId = void 0;
    }
    if (this._outsideSubscribeDeferId !== void 0) {
      clearTimeout(this._outsideSubscribeDeferId);
      this._outsideSubscribeDeferId = void 0;
    }
  }

  /** Focuses the menu trigger. */
  focus(): void {
    this.element.focus();
  }

  private _subscribeOutsidePointerClose(): void {
    this._unsubscribeOutsidePointerClose();
    if (!this.overlayRef) {
      return;
    }
    const pane = this.overlayRef.overlayElement;
    const onPointerDown = (ev: PointerEvent) => {
      if (ev.composedPath().includes(pane)) {
        return;
      }
      this.ngZone.run(() => this.menu.close.emit());
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    this._outsidePointerUnlisten = () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }

  private _unsubscribeOutsidePointerClose(): void {
    if (this._outsidePointerUnlisten) {
      this._outsidePointerUnlisten();
      this._outsidePointerUnlisten = void 0;
    }
  }

  /**
   * This method sets the menu state to open and focuses the first item if
   * the menu was opened via the keyboard.
   */
  private _initMenu(): void {
    this._setIsMenuOpen(true);
    this._setMatMenuPanelOpen(true);
    this.menu.focusFirstItem('mouse');
  }

  private _setMatMenuPanelOpen(isOpen: boolean): void {
    const panel = this.menu;
    if (panel instanceof MatMenu) {
      panel._setIsOpen(isOpen);
    }
  }

  // set state rather than toggle to support triggers sharing a menu
  private _setIsMenuOpen(isOpen: boolean): void {
    this._menuOpen = isOpen;
  }

  /**
   *  This method creates the overlay from the provided menu's template and saves its
   *  OverlayRef so that it can be attached to the DOM when openMenu is called.
   */
  private _createOverlay(event: MouseEvent): OverlayRef {
    if (!this.overlayRef) {
      this._portal = new TemplatePortal(this.menu.templateRef, this.viewContainerRef);
      const config = this._getOverlayConfig(event);
      this.overlayRef = this.overlay.create(config);
    } else {
      this.reposition(<GlobalPositionStrategy>this.overlayRef.getConfig().positionStrategy, event);
    }

    return this.overlayRef;
  }

  /**
   * This method builds the configuration object needed to create the overlay, the OverlayConfig.
   * @returns OverlayConfig
   */
  private _getOverlayConfig(event: MouseEvent): OverlayConfig {
    const overlayConfig = new OverlayConfig();
    overlayConfig.positionStrategy = this._getPosition(event);
    overlayConfig.hasBackdrop = false;
    overlayConfig.scrollStrategy = this.overlay.scrollStrategies.reposition();
    return overlayConfig;
  }

  private _getPosition(event: MouseEvent): GlobalPositionStrategy {
    return this.overlay.position()
      .global()
      .left(toPx(event.clientX))
      .top(toPx(event.clientY));
  }

  private reposition(positionStrategy: GlobalPositionStrategy, event: MouseEvent): void {
    positionStrategy
      .left(toPx(event.clientX))
      .top(toPx(event.clientY));
  }
}
